"use client";

import { c as _c } from "react/compiler-runtime";
import { useAnimatedRating } from './hooks';

// Accessibility Circle Component with animated progress
export const AccessibilityCircle = t0 => {
  const $ = _c(14);
  const {
    score,
    shouldAnimate: t1
  } = t0;
  const shouldAnimate = t1 === undefined ? true : t1;
  const {
    displayValue,
    animatedProgress
  } = useAnimatedRating(score, shouldAnimate);
  const animatedPercentage = animatedProgress;
  const animatedRotation = animatedPercentage / 100 * 360;
  let bgColor;
  if (score >= 76) {
    bgColor = "#059669";
  } else {
    if (score >= 51) {
      bgColor = "#1E5EFF";
    } else {
      if (score >= 26) {
        bgColor = "#6A5ACD";
      } else {
        bgColor = "#F59E0B";
      }
    }
  }
  const t2 = `${bgColor}30`;
  let t3;
  if ($[0] !== t2) {
    t3 = <div className="absolute inset-0 rounded-full" style={{
      border: "5px solid",
      borderColor: t2,
      transition: "border-color 0.6s ease-in-out"
    }} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `conic-gradient(from -90deg, ${bgColor} 0deg, ${bgColor} ${animatedRotation}deg, transparent ${animatedRotation}deg)`;
  let t5;
  if ($[2] !== t4) {
    t5 = <div className="absolute inset-0 rounded-full" style={{
      background: t4,
      clipPath: "circle(50% at 50% 50%)",
      WebkitClipPath: "circle(50% at 50% 50%)"
    }} />;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="absolute rounded-full" style={{
      width: "calc(100% - 10px)",
      height: "calc(100% - 10px)",
      top: "5px",
      left: "5px",
      backgroundColor: "white"
    }} />;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== bgColor) {
    t7 = {
      color: bgColor,
      transition: "color 0.6s ease-in-out"
    };
    $[5] = bgColor;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== displayValue || $[8] !== t7) {
    t8 = <div className="absolute inset-0 flex items-center justify-center z-10"><span className="text-2xl font-bold font-poppins" style={t7}>{displayValue}%</span></div>;
    $[7] = displayValue;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t3 || $[11] !== t5 || $[12] !== t8) {
    t9 = <div className="relative w-32 h-32">{t3}{t5}{t6}{t8}</div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};

// Accessibility Level Indicator Component
export const AccessibilityLevelIndicator = t0 => {
  const $ = _c(18);
  const {
    currentScore
  } = t0;
  const t1 = currentScore >= 0 && currentScore < 25;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      range: "0",
      label: "Very Poor",
      color: "#F59E0B",
      active: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = currentScore >= 25 && currentScore < 50;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      range: "25",
      label: "Poor",
      color: "#6A5ACD",
      active: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = currentScore >= 50 && currentScore < 75;
  let t6;
  if ($[4] !== t5) {
    t6 = {
      range: "50",
      label: "Fair",
      color: "#1E5EFF",
      active: t5
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = currentScore >= 75 && currentScore < 100;
  let t8;
  if ($[6] !== t7) {
    t8 = {
      range: "75",
      label: "Good",
      color: "#00BE52",
      active: t7
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = currentScore >= 100;
  let t10;
  if ($[8] !== t9) {
    t10 = {
      range: "100",
      label: "Excellent",
      color: "#059669",
      active: t9
    };
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t2 || $[12] !== t4 || $[13] !== t6 || $[14] !== t8) {
    t11 = [t2, t4, t6, t8, t10];
    $[10] = t10;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const levels = t11;
  let t12;
  if ($[16] !== levels) {
    t12 = <div className="space-y-3 ml-10">{levels.map(_temp)}</div>;
    $[16] = levels;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
};

// Accessibility Score Display Component
export const AccessibilityScoreDisplay = t0 => {
  const $ = _c(8);
  const {
    accessibilityScore,
    accessibilityCategory,
    shouldAnimate: t1
  } = t0;
  const shouldAnimate = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== accessibilityScore || $[1] !== shouldAnimate) {
    t2 = <AccessibilityCircle score={accessibilityScore} shouldAnimate={shouldAnimate} />;
    $[0] = accessibilityScore;
    $[1] = shouldAnimate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== accessibilityCategory) {
    t3 = <div className="text-sm text-gray-600 font-poppins text-center">{accessibilityCategory}</div>;
    $[3] = accessibilityCategory;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="flex flex-col items-center gap-3">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
function _temp(level, index) {
  return <div key={index} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${level.active ? "bg-gray-50 border border-gray-200" : ""}`}><div className="w-3 h-3 rounded-full flex-shrink-0" style={{
      backgroundColor: level.color
    }} /><div className="flex-1"><span className="text-sm font-semibold text-gray-700 font-poppins">{level.range}</span><span className="text-base font-normal text-gray-700 font-poppins ml-2">{level.label}</span></div></div>;
}